import { useCallback, useEffect, useRef, useState } from "react";

// The deployed main bundle is content-hashed (static/js/main.<hash>.chunk.js),
// so comparing the hash this tab is running against the one in the freshly
// fetched asset-manifest.json tells us whether S3 has a newer build.
const MANIFEST_URL = "/asset-manifest.json";
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

const mainEntryOf = (manifest) => {
  const entry = (manifest?.entrypoints || []).find((f) =>
    /static\/js\/main\..*\.js$/.test(f)
  );
  if (entry) return entry.replace(/^\//, "");
  const file = manifest?.files?.["main.js"];
  return file ? file.replace(/^\//, "") : null;
};

const loadedMainEntry = () => {
  const el = document.querySelector('script[src*="/static/js/main."]');
  if (!el) return null;
  try {
    return new URL(el.src, window.location.href).pathname.replace(/^\//, "");
  } catch (err) {
    return null;
  }
};

/**
 * Detects a new deployment for tabs that are already open.
 *
 * Checks on mount, whenever the tab becomes visible again, and every 5 minutes.
 * The fetch is `cache: "no-store"` so it never reads a stale manifest.
 */
const useAppUpdateCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const currentEntryRef = useRef(loadedMainEntry());

  const check = useCallback(async () => {
    if (!currentEntryRef.current) return;
    try {
      const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return;
      const next = mainEntryOf(await res.json());
      if (next && next !== currentEntryRef.current) setUpdateAvailable(true);
    } catch (err) {
      // Offline or manifest unreachable — nothing to do, the next check retries.
    }
  }, []);

  useEffect(() => {
    check();

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const timer = setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      clearInterval(timer);
    };
  }, [check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload, check };
};

export default useAppUpdateCheck;
